import MouseMode from "./settings/MouseMode";
import PortalDocument, {PortalDocumentInterface} from "../document/PortalDocument";
import React, {createRef, ReactNode, RefObject} from "react";

import ViewerType from "../document/DataType";
import DocumentSettings from "../document/DocumentSettings";
import {Button} from "primereact/button";
import {ViewerMode} from "./ViewerMode";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../PortalContext";
import Immutable from "../../shared/immutable/Immutable";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import NumberHelper from "../../shared/helper/NumberHelper";
import ZoomMode from "./settings/ZoomMode";
import {isMobile} from "react-device-detect";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export const VIEWER_PADDING: number = 30;

export interface AbstractViewerProperties extends I18nGlobals {
	attachmentKey?: string;
	context: PortalContext;
	mouseMode: MouseMode
	viewerMode: ViewerMode;
	portalDocument: Immutable<PortalDocumentInterface>;
	updateScroll?: (xPos: number, yPos: number) => void;
}

export interface AbstractViewerState {
	lastMoveEvent?: MouseEvent;
	moveActive: boolean;
	numberOfPages: number;
}

export default abstract class AbstractViewer<PROPERTY_TYPE extends AbstractViewerProperties = any,
	STATUS_TYPE extends AbstractViewerState = any> extends PortalPureComponent<PROPERTY_TYPE, STATUS_TYPE> {
	protected viewerElement: RefObject<HTMLDivElement>;
	private startTouches?: TouchList;
	private startTouchTime: number;

	constructor(props: Readonly<PROPERTY_TYPE>) {
		super(props);

		this.state = {
			moveActive: false,
			numberOfPages: 0
		} as STATUS_TYPE;

		this.viewerElement = createRef<HTMLDivElement>();

		this.onTouchStart = this.onTouchStart.bind(this);
		this.onTouchMove = this.onTouchMove.bind(this);
		this.onTouchEnd = this.onTouchEnd.bind(this);
		this.handleMouseDown = this.handleMouseDown.bind(this);
		this.handleMouseUp = this.handleMouseUp.bind(this);
		this.handleMouseMove = this.handleMouseMove.bind(this);
		this.handleMouseWheel = this.handleMouseWheel.bind(this);
		this.throttledMousewheelZoom = FunctionHelper.throttle(this.throttledMousewheelZoom.bind(this), 100);

		this.startTouchTime = 0;
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
			this.props.viewerMode === ViewerMode.PORTFOLIO ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(this.props.attachmentKey, "")
			);

			if (FunctionHelper.exists(attachment)) {
				await portalDocument.loadViewerData(attachment);
			}
		} else if (portalDocument.getViewerType() !== ViewerType.DEFAULT) {
			await this.props.context.documentManager.downloadRawData(this.props.portalDocument.get().id!);
			await portalDocument.loadViewerData();
		}

		await this.setNumberOfPages();

		// apply global events so events get applied outside the browser
		window.addEventListener("pointerup", this.handleMouseUp);
		window.addEventListener("pointermove", this.handleMouseMove);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		// cleanup events
		window.removeEventListener("pointerup", this.handleMouseUp);
		window.removeEventListener("pointermove", this.handleMouseMove);
	}

	protected handleMouseDown(event: MouseEvent) {
		if (event.button !== 0 || this.props.mouseMode !== MouseMode.MOVE) {
			return;
		}

		this.setState({
			moveActive: true,
			lastMoveEvent: undefined
		});
	}

	protected handleMouseUp(event: MouseEvent) {
		if (event.button !== 0 || !this.state.moveActive) {
			return;
		}

		this.setState({
			moveActive: false,
			lastMoveEvent: undefined
		});
	}

	protected handleMouseMove(event: MouseEvent) {
		if (!this.state.moveActive) {
			return;
		}

		if (!FunctionHelper.exists(this.state.lastMoveEvent)) {
			this.setState({
				lastMoveEvent: event
			});
			return;
		}

		let lastMoveEvent: MouseEvent = this.state.lastMoveEvent!;
		let offsetX: number = lastMoveEvent.clientX - event.clientX;
		let offsetY: number = lastMoveEvent.clientY - event.clientY;

		this.setState({
			lastMoveEvent: event
		});

		if (offsetX === 0 && offsetY === 0) {
			//skip unnecessary updates
			return;
		}

		if (this.viewerElement.current === null) {
			return;
		}

		let scrollLeft: number = Math.max(0, this.viewerElement.current.scrollLeft + offsetX);
		let scrollTop: number = Math.max(0, this.viewerElement.current.scrollTop + offsetY);

		this.viewerElement.current.scrollLeft = scrollLeft;
		this.viewerElement.current.scrollTop = scrollTop;
	}

	protected async setNumberOfPages(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let numberOfPages: number = 0;
		if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
			this.props.viewerMode === ViewerMode.PORTFOLIO ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			let attachment: Attachment | undefined = portalDocument.getAttachment(
				FunctionHelper.applyValue(this.props.attachmentKey, "")
			);

			if (FunctionHelper.exists(attachment)) {
				numberOfPages = await portalDocument.getNumberOfPages(attachment);
			}
		} else {
			numberOfPages = await portalDocument.getNumberOfPages();
		}

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	protected async throttledMousewheelZoom(event: React.WheelEvent): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value) ||
			!FunctionHelper.exists(this.viewerElement.current)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		// update zoom
		let scaleFactor: number = Math.pow(1.05, -event.deltaY > 0 ? 2 : -2);
		let currentZoom: number = viewerSettings.activeViewer.zoomValue;
		let zoomResult: number = NumberHelper.parseInt(currentZoom * scaleFactor, {min: 10, max: 400});
		if (zoomResult === currentZoom) {
			return;
		}

		// apply zoom value
		viewerSettings.activeViewer.zoomValue = zoomResult;
		viewerSettings.activeViewer.zoomMode = ZoomMode.CUSTOM;
		this.props.context.documentManager.updateGlobalDocumentList();

		// scroll to mouse
		let viewerRect: DOMRect = this.viewerElement.current!.getBoundingClientRect();
		scaleFactor = zoomResult / currentZoom;

		let eventPosX = event.pageX - viewerRect.x;
		let eventPosY = event.pageY - viewerRect.y;

		eventPosX = eventPosX * scaleFactor - eventPosX;
		eventPosY = eventPosY * scaleFactor - eventPosY;

		if (!FunctionHelper.exists(this.viewerElement.current)) {
			return;
		}

		this.viewerElement.current!.scrollLeft = Math.max(
			0, this.viewerElement.current!.scrollLeft * scaleFactor + eventPosX
		);
		this.viewerElement.current!.scrollTop = Math.max(
			0, this.viewerElement.current!.scrollTop * scaleFactor + eventPosY
		);
	}

	protected async handleMouseWheel(event: React.WheelEvent): Promise<void> {
		if (!FunctionHelper.exists(this.viewerElement.current) || (
			this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode === ViewerMode.PORTFOLIO
		)) {
			return;
		}

		if (event.ctrlKey) {
			await this.throttledMousewheelZoom(event);
		}
	}

	protected getTouch(touchList: TouchList, id: number): Touch | null {
		for (let touch of touchList) {
			if (touch.identifier === id) {
				return touch;
			}
		}

		return null;
	}

	protected onTouchStart(event: any): void {
		if (!isMobile || (
			this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO
		)) {
			return;
		}

		this.startTouches = event.touches;
		this.startTouchTime = Date.now();
	}

	protected onTouchMove(event: any): void {
		if (!isMobile || (
			this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO
		)) {
			return;
		}

		if (!FunctionHelper.exists(this.startTouches)) {
			this.startTouches = event.touches;
			this.startTouchTime = Date.now();
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let endTouches: TouchList = event.changedTouches;
		let endTouchTime: number = Date.now();

		// handle pinch
		if (endTouches.length === 2 && this.startTouches?.length === 2) {
			let touchTime: number = endTouchTime - this.startTouchTime;
			if (touchTime < 100) {
				return;
			}

			let firstTouchEnd: Touch | null = endTouches.item(0);
			let secondTouchEnd: Touch | null = endTouches.item(1);
			if (!FunctionHelper.exists(firstTouchEnd) || !FunctionHelper.exists(secondTouchEnd)) {
				return;
			}

			let firstTouchStart: Touch | null = this.getTouch(this.startTouches, firstTouchEnd!.identifier);
			let secondTouchStart: Touch | null = this.getTouch(this.startTouches, secondTouchEnd!.identifier);
			if (!FunctionHelper.exists(firstTouchStart) || !FunctionHelper.exists(secondTouchStart)) {
				return;
			}

			let startTouchWidthDistance: number = Math.abs(firstTouchStart!.clientX - secondTouchStart!.clientX);
			let startTouchHeightDistance: number = Math.abs(firstTouchStart!.clientY - secondTouchStart!.clientY);

			let endTouchWidthDistance: number = Math.abs(firstTouchEnd!.clientX - secondTouchEnd!.clientX);
			let endTouchHeightDistance: number = Math.abs(firstTouchEnd!.clientY - secondTouchEnd!.clientY);

			let scaleFactor: number = 1;

			let startTouchDistance: number = startTouchWidthDistance;
			let endTouchDistance: number = endTouchWidthDistance;
			if (
				Math.abs(endTouchHeightDistance - startTouchHeightDistance) >
				Math.abs(endTouchWidthDistance - startTouchWidthDistance)
			) {
				startTouchDistance = startTouchHeightDistance;
				endTouchDistance = endTouchHeightDistance;
			}

			if (startTouchDistance < endTouchDistance) {
				// pinch in
				scaleFactor = Math.pow(1.05, 2);
			} else if (startTouchDistance > endTouchDistance) {
				// pinch out
				scaleFactor = Math.pow(1.05, -2);
			} else {
				return;
			}

			let currentZoom: number = viewerSettings.activeViewer.zoomValue;
			let zoomResult: number = NumberHelper.parseInt(currentZoom * scaleFactor, {min: 10, max: 400});
			if (zoomResult === currentZoom) {
				return;
			}

			// apply zoom value
			viewerSettings.activeViewer.zoomMode = ZoomMode.CUSTOM;
			viewerSettings.activeViewer.zoomValue = zoomResult;
			this.props.context.documentManager.updateGlobalDocumentList();

			this.startTouches = endTouches;
			this.startTouchTime = endTouchTime;

			// scroll to mouse
			let viewerRect: DOMRect = this.viewerElement.current!.getBoundingClientRect();
			scaleFactor = zoomResult / currentZoom;

			let centerX: number = Math.min(firstTouchEnd!.pageX, secondTouchEnd!.pageX) + (endTouchWidthDistance / 2);
			let centerY: number = Math.min(firstTouchEnd!.pageY, secondTouchEnd!.pageY) + (endTouchHeightDistance / 2);

			let eventPosX = centerX - viewerRect.x;
			let eventPosY = centerY - viewerRect.y;

			eventPosX = eventPosX * scaleFactor - eventPosX;
			eventPosY = eventPosY * scaleFactor - eventPosY;

			if (!FunctionHelper.exists(this.viewerElement.current)) {
				return;
			}

			this.viewerElement.current!.scrollLeft = Math.max(
				0, this.viewerElement.current!.scrollLeft * scaleFactor + eventPosX
			);
			this.viewerElement.current!.scrollTop = Math.max(
				0, this.viewerElement.current!.scrollTop * scaleFactor + eventPosY
			);
		}
	}

	protected onTouchEnd(event: any): void {
		if (!isMobile || (
			this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO
		)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		let endTouches: TouchList = event.changedTouches;
		let endTouchTime: number = Date.now();

		// handle swipe
		if (endTouches.length === 1 && this.startTouches?.length === 1) {
			let touchTime: number = endTouchTime - this.startTouchTime;
			if (touchTime > 200) {
				return;
			}

			let endTouch: Touch | null = endTouches.item(0);
			if (!FunctionHelper.exists(endTouch)) {
				return;
			}

			let startTouch: Touch | null = this.getTouch(this.startTouches, endTouch!.identifier);
			if (!FunctionHelper.exists(startTouch)) {
				return;
			}

			let touchWidth: number = endTouch!.clientX - startTouch!.clientX;
			let touchHeight: number = endTouch!.clientY - startTouch!.clientY;

			if (Math.abs(touchHeight) > Math.abs(touchWidth) || Math.abs(touchWidth) < 50) {
				return;
			}

			if (touchWidth > 0) {
				// swipe right
				viewerSettings.activeViewer.previousPage();
			} else if (touchWidth < 0) {
				// swipe left
				viewerSettings.activeViewer.nextPage(this.state.numberOfPages);
			}

			this.props.context.documentManager.updateGlobalDocumentList();
		}
	}

	protected renderViewerControls(): ReactNode {
		if (this.props.viewerMode !== ViewerMode.DEFAULT && this.props.viewerMode !== ViewerMode.PORTFOLIO) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let viewerSettings: DocumentSettings = portalDocument.getViewerSettings();

		return (
			<>
				{viewerSettings.activeViewer.currentPage > 1 &&
					<div className="viewer-buttons left">
						<Button icon="icon icon-first" onClick={() => {
							viewerSettings.activeViewer.firstPage();
							this.props.context.documentManager.updateGlobalDocumentList();
						}}/>
						<Button icon="icon icon-previous" onClick={() => {
							viewerSettings.activeViewer.previousPage();
							this.props.context.documentManager.updateGlobalDocumentList();
						}}/>
					</div>
				}
				{viewerSettings.activeViewer.currentPage < this.state.numberOfPages &&
					<div className="viewer-buttons right">
						<Button icon="icon icon-next" onClick={async () => {
							viewerSettings.activeViewer.nextPage(this.state.numberOfPages);
							this.props.context.documentManager.updateGlobalDocumentList();
						}}/>
						<Button icon="icon icon-last" onClick={async () => {
							viewerSettings.activeViewer.lastPage(this.state.numberOfPages);
							this.props.context.documentManager.updateGlobalDocumentList();
						}}/>
					</div>
				}
			</>
		);
	}
}